import {
  Button,
  Checkbox,
  FormRow,
  Modal,
  Spacer,
  Stack,
  Text
} from '@efast_public/fjd-component-library';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import {
  IUserWithRightsDtoV2,
  IAvailableRolesDto,
  IOrgUnitRoleDto
} from '../../../../../api/models/Einstellungen/userRoleDto';
import { useUpdateUserRoles } from '../../../../../api/hooks/useUpdateUserGroups';
import { useQuery } from '@tanstack/react-query';
import { BehApiRoutes } from '../../../../../api/beh-api';

interface UsersAndRolesManagementEditModalProps {
  readonly onClose: () => void;
  readonly user: IUserWithRightsDtoV2;
  readonly isLastAdmin: boolean;
}

export default function UsersAndRolesManagementEditModalSSO({
  onClose,
  user,
  isLastAdmin
}: UsersAndRolesManagementEditModalProps) {
  const { t } = useTranslation();

  const [selectedSystemRoles, setSelectedSystemRoles] = useState<string[]>(
    user.systemRoles
  );
  const [selectedOrgUnitRoles, setSelectedOrgUnitRoles] = useState<
    IOrgUnitRoleDto[]
  >(user.orgUnitRoles);

  const { mutateAsync: updateUserRoles, isPending } = useUpdateUserRoles();

  const { data: availableRoles } = useQuery<IAvailableRolesDto>({
    queryKey: [BehApiRoutes.availableRoles()]
  });

  const handleToggleSystemRole = (roleName: string, checked: boolean) => {
    if (checked) {
      setSelectedSystemRoles((prev) => [...prev, roleName]);
    } else {
      setSelectedSystemRoles((prev) => prev.filter((r) => r !== roleName));
    }
  };

  const handleToggleOrgUnitRole = (
    orgUnitName: string,
    roleName: string,
    checked: boolean
  ) => {
    setSelectedOrgUnitRoles((prev) => {
      const existing = prev.find((o) => o.orgUnitName === orgUnitName);
      if (checked) {
        if (existing) {
          return prev.map((o) =>
            o.orgUnitName === orgUnitName
              ? { ...o, roles: [...o.roles, roleName] }
              : o
          );
        }
        return [...prev, { orgUnitName, roles: [roleName] }];
      } else {
        if (existing) {
          const newRoles = existing.roles.filter((r) => r !== roleName);
          if (newRoles.length === 0) {
            return prev.filter((o) => o.orgUnitName !== orgUnitName);
          }
          return prev.map((o) =>
            o.orgUnitName === orgUnitName ? { ...o, roles: newRoles } : o
          );
        }
        return prev;
      }
    });
  };

  const isOrgUnitRoleSelected = (orgUnitName: string, roleName: string) => {
    const orgUnit = selectedOrgUnitRoles.find(
      (o) => o.orgUnitName === orgUnitName
    );
    return orgUnit?.roles.includes(roleName) ?? false;
  };

  const handleSave = async () => {
    await updateUserRoles({
      userId: user.id,
      systemRoles: selectedSystemRoles,
      orgUnitRoles: selectedOrgUnitRoles.filter((o) => o.roles.length > 0)
    });
    onClose();
  };

  const hasAdminSelected = selectedSystemRoles.includes('Administration');

  const canSave = () => {
    if (isLastAdmin) {
      return hasAdminSelected;
    }
    return true;
  };

  const isSaveDisabled = !canSave() || isPending;

  return (
    <Modal open onClose={onClose}>
      <Modal.Header id="UsersAndRolesManagementEditModalSSO-header">
        {t('usersAndRoles.editUserButton')}
      </Modal.Header>

      <Modal.Content>
        <Stack spacing="l">
          <Stack direction="column" spacing="xs">
            <Text style={{ fontWeight: 'bold' }}>{user.email}</Text>
            <Text>
              {`${user.lastName || ''}, ${user.firstName || ''} `.trim()}
            </Text>
          </Stack>

          <Spacer />

          <Stack direction="column" spacing="l">
            {availableRoles && availableRoles.systemRoles.length > 0 && (
              <FormRow label="Systemrollen">
                <Stack direction="column" spacing="s">
                  {availableRoles.systemRoles.map((role) => (
                    <Checkbox
                      key={role}
                      id={`system-role-${role}`}
                      label={role}
                      checked={selectedSystemRoles.includes(role)}
                      onChange={(e) =>
                        handleToggleSystemRole(role, e.currentTarget.checked)
                      }
                      disabled={isPending}
                    />
                  ))}
                </Stack>
              </FormRow>
            )}

            {availableRoles &&
              availableRoles.orgUnitRoles
                .sort((a, b) => a.orgUnitName.localeCompare(b.orgUnitName))
                .map((orgUnit) => (
                  <FormRow
                    key={orgUnit.orgUnitName}
                    label={
                      orgUnit.orgUnitName.charAt(0).toUpperCase() +
                      orgUnit.orgUnitName.slice(1)
                    }
                  >
                    <Stack direction="column" spacing="s">
                      {orgUnit.roles.map((role) => (
                        <Checkbox
                          key={`${orgUnit.orgUnitName}-${role}`}
                          id={`orgunit-role-${orgUnit.orgUnitName}-${role}`}
                          label={role}
                          checked={isOrgUnitRoleSelected(
                            orgUnit.orgUnitName,
                            role
                          )}
                          onChange={(e) =>
                            handleToggleOrgUnitRole(
                              orgUnit.orgUnitName,
                              role,
                              e.currentTarget.checked
                            )
                          }
                          disabled={isPending}
                        />
                      ))}
                    </Stack>
                  </FormRow>
                ))}
          </Stack>

          {isLastAdmin && !hasAdminSelected && (
            <Text variant="error">
              Sie sind der letzte Administrator. Mindestens eine
              Administrator-Rolle muss erhalten bleiben
            </Text>
          )}
        </Stack>
      </Modal.Content>

      <Modal.Footer>
        <Stack direction="row" spacing="m" justify="end">
          <Button variant="primary-text" onClick={onClose} disabled={isPending}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isSaveDisabled || isPending}
          >
            Speichern
          </Button>
        </Stack>
      </Modal.Footer>
    </Modal>
  );
}
